import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { usePersonNames } from '../usePersonNames';
import type { PersonnelDetail, PersonnelReference } from '../personnelTypes';

/**
 * Character references — people who can vouch for the employee.
 *
 * **Captured from the hospital system's own Reference module**, with one thing changed: that
 * model keeps the referee's name in its main database, and here a referee is a subject in their
 * own right. The row carries a `personId`, the names live in `rsys_pii`, and this panel composes
 * the two — a list of rows carrying ids, then ONE batch resolve for the page on screen.
 *
 * **So the section cannot be sorted or searched by name, and it says so.** The placeholder reads
 * "Search occupation, company or address" rather than the usual "Search …". Sorting by name would
 * mean resolving the whole list before paging it; searching one would mean copying names into
 * `rsys_`, which is the copy the split exists to prevent. This is the same rule the personnel
 * list's own anchor column and the family panel both follow — do not "fix" it.
 *
 * **In the order they were RECORDED.** Every other history here sorts on a date the record
 * carries; a reference has none, and the one value a reader would sort by is in the other
 * database. The creation order is how a list of referees reads on paper.
 */
export function ReferencesPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelReference) => void;
  onDelete: (row: PersonnelReference) => void;
  onBulkDelete: (rows: PersonnelReference[]) => void;
  onRestore: (row: PersonnelReference) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.references : record.references.filter((x) => x.isActive)),
    [record.references, showDeleted],
  );

  /*
    Resolved for the WHOLE list rather than the page on screen.

    The list arrives with the record and the table pages it in memory, so resolving per page
    would re-hit the endpoint on every page change for ids already to hand. A handful of referees
    is comfortably inside the batch cap.
  */
  const personIds = useMemo(() => rows.map((x) => x.personId), [rows]);
  const names = usePersonNames(personIds);

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      // Deliberately NOT the name — see the note on the component.
      const matched = term
        ? rows.filter((x) =>
            [x.occupation, x.company, x.address, x.contactNumber]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      /*
        No sort is the RECORDED order, which is what the server sent. Only the local columns can
        be sorted at all; the name has no sort control to offer.
      */
      const field = query.sort?.replace(/^-/, '') ?? null;
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted =
        field === null
          ? matched
          : [...matched].sort((a, b) => {
              const pick = (row: PersonnelReference) =>
                field === 'company'
                  ? (row.company ?? '')
                  : field === 'address'
                    ? (row.address ?? '')
                    : field === 'contactNumber'
                      ? (row.contactNumber ?? '')
                      : (row.occupation ?? '');

              return String(pick(a)).localeCompare(String(pick(b))) * direction;
            });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelReference>({ fetchPage, pageSize: 10 });

  const columns: DataColumn<PersonnelReference>[] = [
    {
      /*
        THE ANCHOR, and neither sortable nor searchable — the panel must not offer either. The
        value lives in another database.

        The three absences are distinguished rather than collapsed to a blank: a resolve still in
        flight, a resolve that failed, and a person the store has erased are different facts, and
        rendering all three the same turns two of them into what looks like a bug.
      */
      key: 'name',
      header: 'Name',
      width: 240,
      render: (x) => {
        if (names.failed) return <TextCell value="Unavailable" />;
        if (names.statusOf(x.personId) === 'erased') return <TextCell value="Erased" />;

        return <TextCell value={names.nameOf(x.personId) ?? (names.loading ? '…' : 'Unknown')} />;
      },
    },
    {
      key: 'occupation',
      header: 'Occupation',
      width: 180,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.occupation ?? null} />,
    },
    {
      key: 'company',
      header: 'Company',
      width: 200,
      sortable: true,
      summary: true,
      /*
        A plain stored value pointing at nothing — a referee's employer is almost never this
        organization, so it binds no catalog and no org unit.
      */
      render: (x) => <TextCell value={x.company ?? null} />,
    },
    {
      key: 'address',
      header: 'Address',
      width: 260,
      sortable: true,
      render: (x) => <TextCell value={x.address ?? null} />,
    },
    {
      key: 'contactNumber',
      header: 'Contact number',
      width: 160,
      sortable: true,
      // A contact number is a machine value, like every other identifier in the product.
      render: (x) => <TextCell value={x.contactNumber ?? null} mono />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row carries
        no marker while Show deleted is on, which is the one state a row must never be silent
        about.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelReference>
        id="personnel-reference"
        state={state}
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Reference', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        /*
          NOT "Search …". The name is in the other database, so saying what this searches is the
          difference between a control that looks broken and one that is honest about its reach.
        */
        searchPlaceholder="Search occupation, company or address"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-references',
              label: `Delete ${deletable.length} reference${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected reference is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No references recorded yet."
        emptySearchMessage="No reference matches that search."
      />
    </ProfileSection>
  );
}
